import {
  IonButton,
  IonContent,
  IonInput,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Icon, Text } from '@/components/memo';
import { SelectModal } from '@/components/modals';
import { useDentist } from '@/contexts';
import {
  HmoProvider,
  createHmoProvider,
  deleteHmoProvider,
  getHmoProviderOptions,
  getHmoProvidersByDentistId,
  updateHmoProvider
} from '@/data/HmoProviders';
import { useModal, useToast } from '@/hooks';
import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { arrowBackOutline, checkmarkCircleOutline } from 'ionicons/icons';

import './UpdateInsurance.css';

interface UpdateInsuranceProps {
  isOpen: boolean;
  onClose: () => void;
  presentingElement?: HTMLElement | undefined;
}

interface CreateHmoProviderInput {
  name: string;
  dentist_id: string;
}

interface UpdateHmoProviderInput {
  id: string;
  hmoProvider: HmoProvider;
}

const UpdateInsurance: React.FC<UpdateInsuranceProps> = ({
  isOpen,
  onClose,
  presentingElement
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist } = useDentist();
  const queryClient = useQueryClient();

  const [hmoProviders, setHmoProviders] = useState<HmoProvider[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedHmoProviders, setLastSavedHmoProviders] = useState<
    HmoProvider[]
  >([]);
  const {
    isOpen: isSelectModalOpen,
    onOpen: onOpenSelectModal,
    onClose: onCloseSelectModal
  } = useModal();
  const [providesInsurance, setProvidesInsurance] = useState<boolean | null>(
    null
  );

  const modal = useRef<HTMLIonModalElement | null>(null);

  const {
    data: initialHmoProviders = [],
    isLoading: loading,
    isFetched: loaded
  }: UseQueryResult<HmoProvider[], Error> = useQuery({
    queryKey: ['hmoProviders', dentist?.id],
    queryFn: () => getHmoProvidersByDentistId(dentist?.id!),
    enabled: isOpen && !!dentist?.id
  });

  useEffect(() => {
    if (loaded) {
      setHmoProviders(initialHmoProviders);
      setLastSavedHmoProviders(initialHmoProviders);
      // Set providesInsurance based on whether there are existing HMO providers
      setProvidesInsurance(initialHmoProviders.length > 0);
    }
  }, [initialHmoProviders, loaded]);

  const { data: hmoProviderOptions = [] }: UseQueryResult<string[], Error> =
    useQuery({
      queryKey: ['hmoProviderOptions'],
      queryFn: getHmoProviderOptions,
      enabled: isOpen
    });

  const createHmoProviderMutation: UseMutationResult<
    HmoProvider | null,
    Error,
    CreateHmoProviderInput
  > = useMutation({
    mutationFn: (input: CreateHmoProviderInput) => createHmoProvider(input),
    onError: () => {
      displayToast({
        message: 'Failed to create HMO provider. Please try again.',
        duration: 3000,
        position: 'bottom'
      });
    }
  });

  const updateHmoProviderMutation: UseMutationResult<
    HmoProvider | null,
    Error,
    UpdateHmoProviderInput
  > = useMutation({
    mutationFn: ({ id, hmoProvider }: UpdateHmoProviderInput) =>
      updateHmoProvider(id, hmoProvider),
    onError: () => {
      displayToast({
        message: 'Failed to update HMO provider. Please try again.',
        duration: 3000,
        position: 'bottom'
      });
    }
  });

  const deleteHmoProviderMutation: UseMutationResult<void, Error, string> =
    useMutation({
      mutationFn: deleteHmoProvider,
      onError: () => {
        displayToast({
          message: 'Failed to delete HMO provider. Please try again.',
          duration: 3000,
          position: 'bottom'
        });
      }
    });

  const copy = {
    subHeader: 'INSURANCE',
    header: (
      <>
        Manage your <Text color="primary">insurance</Text> providers
      </>
    ),
    message: `Do you accept insurance? If yes, please select all the HMO providers you work with.`,
    buttonText: 'Update',
    skipText: 'Skip',
    insuranceQuestion: 'Do you accept insurance?',
    inputLabel: 'HMO Providers',
    inputPlaceholder: 'Example: Maxicare',
    modalTitle: (
      <>
        Select <Text color="primary">HMO providers</Text>
      </>
    )
  };

  const isSelected = (value: string) =>
    hmoProviders.some((provider) => provider.name === value);

  const isPreSelected = (value: string) =>
    lastSavedHmoProviders.some((provider) => provider.name === value);

  const onSelect = (value: string, selected: boolean) => {
    setHmoProviders((prevProviders) =>
      selected
        ? prevProviders.filter((provider) => provider.name !== value)
        : [...prevProviders, { name: value }]
    );
  };

  const onCloseSelectModalWithSave = () => {
    onCloseSelectModal();
    setLastSavedHmoProviders(hmoProviders);
  };

  const onInsuranceToggle = (value: boolean) => {
    setProvidesInsurance(value);
    if (!value) {
      // If user selects "No", clear all HMO providers
      setHmoProviders([]);
    }
  };

  const onNext = useCallback(async () => {
    if (dentist?.id) {
      setHasError(false);
      try {
        if (providesInsurance) {
          // If user provides insurance, save the selected HMO providers
          await Promise.all(
            hmoProviders.map(async (provider) => {
              const existingProvider = initialHmoProviders.find(
                (initialProvider) => initialProvider.name === provider.name
              );
              if (existingProvider?.id) {
                await updateHmoProviderMutation.mutateAsync({
                  id: existingProvider.id,
                  hmoProvider: provider
                });
              } else if (dentist?.id) {
                await createHmoProviderMutation.mutateAsync({
                  name: provider.name,
                  dentist_id: dentist.id
                });
              }
            })
          );

          // Delete HMO providers that were removed
          const providersToDelete = initialHmoProviders
            .filter(
              (provider) => !hmoProviders.some((p) => p.name === provider.name)
            )
            .filter(
              (provider): provider is HmoProvider & { id: string } =>
                provider.id !== null && provider.id !== undefined
            );

          await Promise.all(
            providersToDelete.map((provider) =>
              deleteHmoProviderMutation.mutateAsync(provider.id)
            )
          );
        } else {
          // If user doesn't provide insurance, delete all existing HMO providers
          const providersToDelete = initialHmoProviders.filter(
            (provider): provider is HmoProvider & { id: string } =>
              provider.id !== null && provider.id !== undefined
          );

          await Promise.all(
            providersToDelete.map((provider) =>
              deleteHmoProviderMutation.mutateAsync(provider.id)
            )
          );
        }

        await queryClient.invalidateQueries({
          queryKey: ['hmoProviders', dentist.id]
        });

        displayToast({
          message: 'Insurance settings updated successfully.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
        onClose();
      } catch {
        displayToast({
          message: 'Something went wrong. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    }
  }, [
    providesInsurance,
    hmoProviders,
    initialHmoProviders,
    dentist?.id,
    updateHmoProviderMutation,
    createHmoProviderMutation,
    deleteHmoProviderMutation,
    queryClient,
    onClose
  ]);

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="update-insurance">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={onClose}
            />
            <Spacer top={16} bottom={24}>
              <h1 className="update-insurance__header">{copy.header}</h1>
              <p className="update-insurance__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="update-insurance__form">
                <div className="update-insurance__input">
                  <IonSelect
                    label={copy.insuranceQuestion}
                    labelPlacement="stacked"
                    placeholder="Select an option"
                    value={providesInsurance}
                    onIonChange={(e: CustomEvent) =>
                      onInsuranceToggle(e.detail.value)
                    }
                  >
                    <IonSelectOption value={true}>Yes</IonSelectOption>
                    <IonSelectOption value={false}>No</IonSelectOption>
                  </IonSelect>
                </div>

                {providesInsurance && (
                  <div
                    className={`update-insurance__input ${hasError ? 'update-insurance__input--error' : ''}`}
                  >
                    <IonInput
                      id="hmo-provider-input"
                      label={copy.inputLabel}
                      labelPlacement="stacked"
                      value={hmoProviders
                        .map((provider) => provider.name)
                        .join(', ')}
                      placeholder={copy.inputPlaceholder}
                      onClick={() => {
                        setHasError(false);
                        onOpenSelectModal();
                      }}
                      readonly
                    />
                  </div>
                )}
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                id="next-button"
                onClick={onNext}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={
                  loading ||
                  providesInsurance === null ||
                  (providesInsurance && hmoProviders.length === 0) ||
                  createHmoProviderMutation.isPending ||
                  updateHmoProviderMutation.isPending ||
                  deleteHmoProviderMutation.isPending
                }
              >
                <Icon slot="start" icon={checkmarkCircleOutline} />
                {copy.buttonText}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
        <IonLoading
          isOpen={
            loading ||
            createHmoProviderMutation.isPending ||
            updateHmoProviderMutation.isPending ||
            deleteHmoProviderMutation.isPending
          }
          message="Loading..."
          spinner="crescent"
        />
        <SelectModal
          isOpen={isSelectModalOpen}
          // TODO: presentingElement is temporarily set to undefined due to an issue with Ionic modal card state when used with IonTabs.
          // Uncomment the line below and remove presentingElement={undefined} once the issue is resolved.
          // presentingElement={modal.current}
          presentingElement={undefined}
          title={copy.modalTitle}
          options={hmoProviderOptions}
          isSelected={isSelected}
          isPreSelected={isPreSelected}
          onSelect={onSelect}
          onClose={onCloseSelectModalWithSave}
          multiple={true}
          showOthers={false}
        />
      </IonContent>
    </IonModal>
  );
};

export default UpdateInsurance;
